import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useIsDoctor } from './useIsDoctor';

/**
 * Doctor-only mode (roadmap #11, Samuel 2026-07-16): internal doctors log in
 * to the SAME app, but see a reduced experience - only "My Patients" and the
 * "Daily List", never the Scheduler/Referrals/Inventory/Tasks/Admin tabs.
 *
 * Rule: a login is doctor-only when its linked staff record is a doctor
 * (staff.is_doctor, with the role-name fallback in useIsDoctor) AND the user
 * account is not privileged. Admins and operations managers keep the full
 * app even if they are also doctors (e.g. Dr Ang's admin login), while a
 * doctor's own office_user login gets the focused view.
 *
 * First wave: Dr Richard Ang + Dr Surein (trial).
 */
export const useDoctorOnlyMode = (): { doctorOnly: boolean; isLoading: boolean } => {
  const { user } = useAuth();
  const { isDoctor, isLoading } = useIsDoctor();
  const privileged = user?.role === 'admin' || user?.role === 'operations_manager';
  return { doctorOnly: isDoctor && !privileged, isLoading };
};

/**
 * Route wrapper: sends doctor-only users to /my-patients instead of the
 * wrapped page. Used for pages that doctor-only accounts should never see.
 */
export const DoctorGate = ({ children }: { children: React.ReactElement }): React.ReactElement | null => {
  const { doctorOnly, isLoading } = useDoctorOnlyMode();
  if (isLoading) return null;
  if (doctorOnly) return React.createElement(Navigate, { to: '/my-patients', replace: true });
  return children;
};
